import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
} from '@mui/material';
import CustomPagination from '../../../components/common/CustomPagination';
import style from './Advance.module.css';
import { getAdvanceReceiptURL, getRecievedRequestPatientList, ReceivedAdvanceListResponse, AdvanceReceiptData } from './advance.helper';
import TableSkeleton from '../../../components/common/TableSkeleton';
import { useAppQuery } from '../../../services/appQuery';
import { getRequest } from '../../../services/httpService';
import { convertDateToDDMMYYYY } from '../../../utils/commonUtils';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PrintIcon } from '../../../assets/SVG/printerblue.svg';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { usePatientContext } from '../../../components/patinetProvider';
import ReusableReportComponent from '../../../components/printReports/ReusableReportComponent';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../components/alertProvider';
import ReceiptComponent from './ReceiptComponent';
import TruncatedText from '../../../utils/TruncatedText';
import { useTranslation } from 'react-i18next';



const formatMoney = (val: number) => val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdvanceLogs: React.FC = () => {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { patientId } = usePatientContext();
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [receiptData, setReceiptData] = useState<AdvanceReceiptData | null>(null);
  const { addAlert } = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  const [pageSize, setPageSize] = useState(10);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

    
  const { data: ReceivedAdvanceRequests, isLoading} = useAppQuery<ReceivedAdvanceListResponse>(
    [
      'ReceivedAdvanceList',
      page,
      pageSize,
      patientId
    ],
    () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      params.set('patient_id', String(patientId));
      return getRequest<ReceivedAdvanceListResponse>(`${getRecievedRequestPatientList()}?${params.toString()}`);
    },
    { staleTime: 5 * 60 * 1000, refetchOnMount: true }
  );


  
  const handleClose = () => { setShowPrint(false); };
  
  const { mutate: fetchReceipt, isPending: isPrinting } = useMutation({
    mutationFn: (creditId: number): Promise<AdvanceReceiptData> => getRequest(getAdvanceReceiptURL(creditId)),
    onSuccess: (data:AdvanceReceiptData) => {
      setReceiptData(data);
      setShowPrint(true);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('Advance.messages.failed_to_load_receipt', { defaultValue: 'Failed to load receipt.' });
      addAlert(message, 'error');
    },
  });
  return (
    <>
      <HeaderProfile />
      <Box sx={{ py: 3, px: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{tBilling('Advance.advance_log')}</Typography>
        </Stack>
      </Box>
      <Box sx={{ pb: 3, px: 2 }} data-testid="advance-logs-container">
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="advance-logs-table-container">
          <Table aria-label={tBilling('Advance.aria.received_list_table', { defaultValue: 'Received list' })} data-testid="advance-logs-table">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.date')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.receipt_no')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.clinic')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.against')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.package_name')}</TableCell>
                <TableCell className={style.tableHeader} align="center" sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.total_amt')}</TableCell>
                <TableCell className={style.tableHeader} align="center" sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.used_amt')}</TableCell>
                <TableCell className={style.tableHeader} align="center" sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.balance_amt')}</TableCell>
                <TableCell className={style.tableHeader} align="center" sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.refund_amt')}</TableCell>
                <TableCell className={style.tableHeader}>{tBilling('Advance.package_bill_freeze')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.remark')}</TableCell>
                <TableCell className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>{tBilling('Advance.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableSkeleton
                  columnWidths={[260, 200, 160, 140, 120, 120, 120, 120, 160, 200, 240, 120, 140]}
                  rows={pageSize} />
              ) : (
                <>
                  {ReceivedAdvanceRequests?.objects.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell className={style.tableCells} sx={{ minWidth: 140 }}>{row?.transaction_date ? convertDateToDDMMYYYY(row.transaction_date) : '-'}</TableCell>
                      <TableCell className={style.tableCells} sx={{ minWidth: 200 }}>{row?.receipt_no ?? '-'}</TableCell>
                      <TableCell className={style.tableCells} sx={{ minWidth: 160 }}>{row?.clinic}</TableCell>
                      <TableCell className={style.tableCells} sx={{ minWidth: 200 }}>{row?.advance_against ?? '-'}</TableCell>
                      <TableCell className={style.tableCells} sx={{ minWidth: 240 }}>{row?.package_name ?? '-'}</TableCell>
                      <TableCell className={style.tableCells} align="center">{formatMoney(row?.total_amount)}</TableCell>
                      <TableCell className={style.tableCells} align="center">{formatMoney(row?.used_amount)}</TableCell>
                      <TableCell className={style.tableCells} align="center">{formatMoney(row?.balance_amount)}</TableCell>
                      <TableCell className={style.tableCells} align="center">{formatMoney(row?.refund_amount)}</TableCell>
                      <TableCell align="center" className={style.tableCells} sx={{ width: 120 }}>
                        {row?.package_bill_freeze ? tBilling('Advance.yes', { defaultValue: 'Yes' }) : tBilling('Advance.no', { defaultValue: 'No' })}
                      </TableCell>
                      <TableCell className={style.tableCells} sx={{ width: 160 }}>
                        {row?.remark ? <TruncatedText text={row.remark} title={tBilling('Advance.remark')} iconPosition="left" /> : <Typography variant="body2">-</Typography>}
                      </TableCell>
                      <TableCell align="right" className={style.tableCells} sx={{ width: 140 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" aria-label={tBilling('Advance.aria.print_receipt', { defaultValue: 'Print receipt' })} onClick={() => fetchReceipt(row.credit_transaction_id)} disabled={isPrinting}>
                            <PrintIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {ReceivedAdvanceRequests?.objects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13}>
                        <Box sx={{textAlign: 'center', color: 'text.secondary' }}>
                          {tBilling('Advance.no_entries_found')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 15, 25, 50]}
          onPageSizeChange={handlePageSizeChange}
          totalCount={ReceivedAdvanceRequests?.meta.total_count ?? 0}
          onPageChange={(_, newPage) => setPage(newPage)} />
      </Box>

      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={tBilling('Advance.advance_receipt', { defaultValue: 'Advance Receipt' })}
      >
        {receiptData ? (
          <ReceiptComponent data={receiptData} />
        ) : (
          <Typography variant="h6" sx={{ mb: 2 }}>{tBilling('Advance.messages.no_receipt_data_available', { defaultValue: 'No receipt data available.' })}</Typography>
        )}
      </ReusableReportComponent>
    </>
  );
};

export default AdvanceLogs;
